import { useEffect } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AccountResponse, fetchAccount } from 'api/account';
import { Settings } from 'constants/settings';
import { useCurrentAccountId } from 'customHooks/useCurrentAccountId';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';

type TransformedAccount = ReturnType<typeof useCurrentAccount>;

const useCurrentAccount = () => {
    const accountId = useCurrentAccountId();
    const { data: account } = useSuspenseQuery({
        queryKey: accountQueryKeys.current(accountId),
        queryFn: () => fetchAccount(accountId),
        select: transformAccount,
        refetchOnWindowFocus: 'always',
    });

    useEffect(() => {
        Sentry.setTag('account', accountId);
    }, [accountId]);

    return account;
};

const transformAccount = (data: AccountResponse) => {
    const { data: account, menu } = data;
    const {
        id,
        phone,
        email,
        timezone,
        type,
        reader_purchase_url: readerUrl,
        settings,
        bcc_address,
        forward_address,
        has_integrations: hasIntegrations,
        has_texting_access: hasTextingAccess,
        custom_fields,
        logo,
        email_logo,
        name,
        tagline,
        website,
        linkedin,
        twitter,
        snapchat,
        instagram,
        youtube,
        facebook,
        slug,
        description,
        mission,
        cover,
        remaining_free_mail_proof,
        first_inclusive_transaction_at,
        subscriptions,
        data_hygiene,
        trial_started_at,
        trial_ends_at,
        billing_email,
        stripe_id,
        former_stripe_account,
        default_payout_tip,
        instant_eligible,
        has_used_instant_payouts,
        questionnaire,
        count,
    } = account;

    const {
        is_activated,
        postal_address,
        aid: accountAid,
        is_verified: isVerified,
        campaigns,
        legal_entity,
        sponsoring_legal_entity,
        sponsorship_status,
        title,
        referral_url: referralLink,
        is_beta: isBeta,
        access,
        can_accept_payments: canAcceptPayments,
        is_payments_blocked: arePaymentsBlocked,
        has_giving_hub: hasGivingHub,
        has_failing_payouts: hasFailingPayouts,
        payment_account: paymentAccount,
        tasks,
        recent_campaigns: recentCampaigns,
        pinned_campaigns: pinnedCampaigns,
        pinned_segments: pinnedSegments,
        beefree_auth_payload: beefreeAuthPayload,
        beefree_custom_css: beefreeCustomCss,
        editor_custom_css: editorCustomCss,
        search_settings,
    } = menu;

    const ein = legal_entity?.ein;
    const isEinVerified = !!ein || isVerified;
    const recurringPaymentsSetting = settings.find(
        item => item.code === Settings.EnableRecurringPayments.Code,
    );
    const isSponsoredApproved =
        ['approved', 'unclaimed'].includes(sponsoring_legal_entity?.status ?? '') &&
        sponsorship_status === 'approved';
    const isRecurringPaymentsEnabled = !!recurringPaymentsSetting?.active;
    const canPinCampaigns = campaigns.filter(campaign => campaign.is_pinned).length < 5;
    const validGivebutterPlusPlan = subscriptions.find(s => s.type === 'plus' && s.is_valid);

    const isCurrentlyOnFreeTrial =
        account.trial_ends_at !== null && isoDateFromServer(account.trial_ends_at) > DateTime.now();
    const isFreeTrialExpired =
        account.trial_ends_at !== null && isoDateFromServer(account.trial_ends_at) < DateTime.now();
    const isTrialEndedWithin24Hours =
        account.trial_ends_at !== null &&
        isoDateFromServer(account.trial_ends_at) > DateTime.now().minus({ days: 1 });

    return {
        access,
        accountId: id,
        accountAid,
        postalAddress: postal_address,
        email,
        ein,
        phone,
        name,
        isAccountVerified: isVerified,
        isAccountSponsoredOrVerified: isSponsoredApproved || isVerified,
        isEinVerified,
        isBeta,
        timezone,
        type,
        campaigns,
        readerUrl,
        legalEntity: legal_entity,
        sponsoringLegalEntity: sponsoring_legal_entity,
        sponsorshipStatus: sponsorship_status,
        settings,
        title,
        isRecurringPaymentsEnabled,
        recurringPaymentsSetting,
        customFields: custom_fields,
        hasIntegrations,
        hasTextingAccess,
        referralLink,
        canAcceptPayments,
        arePaymentsBlocked,
        hasGivingHub,
        emailConfig: {
            bccAddress: bcc_address,
            forwardAddress: forward_address,
        },
        logo,
        emailLogo: email_logo,
        hasFailingPayouts,
        paymentAccount,
        tasks,
        is_activated,
        email_logo,
        tagline,
        website,
        linkedin,
        twitter,
        snapchat,
        instagram,
        youtube,
        facebook,
        slug,
        mission,
        description,
        cover,
        recentCampaigns,
        pinnedCampaigns,
        pinnedSegments,
        canPinCampaigns,
        remainingFreeMailProof: remaining_free_mail_proof,
        firstInclusiveTransactionAt: first_inclusive_transaction_at,
        dataHygiene: data_hygiene,
        beefreeAuthPayload,
        beefreeCustomCss,
        editorCustomCss,
        search_api_key: search_settings?.search_key,
        search_api_url: search_settings?.url,
        searchCollections: search_settings?.collections,
        searchAllowedResources: search_settings ? Object.keys(search_settings.collections) : [],
        subscriptions,
        validGivebutterPlusPlan,
        trialStartedAt: trial_started_at,
        trialEndsAt: trial_ends_at,
        isCurrentlyOnFreeTrial,
        isFreeTrialExpired,
        isTrialEndedWithin24Hours,
        hasGivebutterPlusAccess: !!validGivebutterPlusPlan || isCurrentlyOnFreeTrial,
        billingEmail: billing_email,
        stripeId: stripe_id,
        formerStripeAccount: former_stripe_account,
        defaultPayoutTip: default_payout_tip,
        instantEligible: instant_eligible,
        hasUsedInstantPayouts: has_used_instant_payouts,
        questionnaire,
        numContacts: count.contacts,
    };
};

export { useCurrentAccount, transformAccount };
export type { TransformedAccount };
